import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormikProvider, useFormik } from 'formik';

import pngFundo from 'assets/images/png-fundo.png';
import DialogForm from 'components/DialogForm';
import DialogHeader from 'components/DialogHeader';
import {
  MensagemCtaInterface,
  TiposDeCabecalhoMensagemInterativaEnum,
  TiposDeMensagemInterativaEnum,
} from 'store/reducers/mensagens';
import * as validators from 'utils/validators';

import { PreviewMensagemCtaUrl } from './components/PreviewMensagemCtaUrl';
import ActionFormFields from './form/ActionFormFields';
import BodyFormFields from './form/BodyFormFields';
import HeaderFormFields from './form/HeaderFormFields';

const useStyles = makeStyles(theme => ({
  '@global': {
    'input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
      WebkitTextFillColor: theme.palette.text.primary,
    },
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    paddingBottom: theme.spacing(3),
    minHeight: 600,
  },
  grid: {
    minHeight: 600,
    '& > :last-child': {
      paddingLeft: theme.spacing(2),
    },
  },
  previewContainer: {
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: theme.palette.type === 'light' ? '#f4f2eb' : '#2b2b2b',
    borderRadius: theme.shape.borderRadius,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: 26,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `url(${pngFundo})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      opacity: theme.palette.type === 'light' ? 1 : 0.1,
      zIndex: 0,
    },
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
  },
  dialogActions: {
    padding: theme.spacing(1, 3, 2),
  },
}));

interface ModalEnviarMensagemCTAProps {
  open: boolean;
  onSendInteractiveMessage?: (mensagemCta?: MensagemCtaInterface) => void;
  onClose: () => void;
}

export default function ModalEnviarMensagemCTA({
  onSendInteractiveMessage,
  open,
  onClose,
}: ModalEnviarMensagemCTAProps) {
  const classes = useStyles();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      tipoCabecalho: null,
      textoCabecalho: '',
      imagemCabecalho: '',
      videoCabecalho: '',
      documentoCabecalho: '',
      mensagem: '',
      rodape: '',
      rotuloDoBotao: '',
      urlCta: '',
    },
    validationSchema: validators.criarMensagemDeUrlCta,
    onSubmit: values => {
      const data: MensagemCtaInterface = {
        type: TiposDeMensagemInterativaEnum.CTA_URL,
        action: {
          name: TiposDeMensagemInterativaEnum.CTA_URL,
          parameters: {
            display_text: values.rotuloDoBotao,
            url: values.urlCta,
          },
        },
        body: {
          text: values.mensagem,
        },
        ...(values.rodape && {
          footer: {
            text: values.rodape,
          },
        }),
        ...getCabecalho(values),
      };

      onSendInteractiveMessage?.(data);
      onClose();
    },
  });

  const getCabecalho = (values: typeof formik.values) => {
    const { tipoCabecalho } = values;
    if (!tipoCabecalho) return {};

    switch (tipoCabecalho) {
      case 'text':
        return {
          header: {
            type: TiposDeCabecalhoMensagemInterativaEnum.TEXTO,
            text: values.textoCabecalho,
          },
        };
      case 'image':
        return values.imagemCabecalho
          ? {
              header: {
                type: TiposDeCabecalhoMensagemInterativaEnum.IMAGEM,
                image: { link: values.imagemCabecalho },
              },
            }
          : {};
      case 'video':
        return values.videoCabecalho
          ? {
              header: {
                type: TiposDeCabecalhoMensagemInterativaEnum.VIDEO,
                video: { link: values.videoCabecalho },
              },
            }
          : {};
      case 'document':
        return values.documentoCabecalho
          ? {
              header: {
                type: TiposDeCabecalhoMensagemInterativaEnum.DOCUMENTO,
                document: { link: values.documentoCabecalho },
              },
            }
          : {};
      default:
        return {};
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionProps={{ onExited: () => formik.resetForm() }}
    >
      <FormikProvider value={formik}>
        <DialogForm
          onSubmit={formik.handleSubmit}
          loading={formik.isSubmitting}
        >
          <DialogHeader title="Mensagem CTA URL" onClose={onClose} />

          <DialogContent className={classes.dialogContent}>
            <Grid container spacing={2} className={classes.grid}>
              <Grid item xs={8}>
                <Box display="flex" flexDirection="column" gridGap={16}>
                  <HeaderFormFields />
                  <BodyFormFields />
                  <ActionFormFields />
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box className={classes.previewContainer}>
                  <PreviewMensagemCtaUrl
                    type={formik.values.tipoCabecalho}
                    header={formik.values.textoCabecalho}
                    body={{
                      text: formik.values.mensagem,
                      caption: formik.values.rodape,
                    }}
                    buttonAction={{
                      label: formik.values.rotuloDoBotao,
                      url: formik.values.urlCta,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions className={classes.dialogActions}>
            <Button onClick={onClose} color="default">
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={formik.isSubmitting}
              type="submit"
            >
              Enviar
            </Button>
          </DialogActions>
        </DialogForm>
      </FormikProvider>
    </Dialog>
  );
}
